<template>
  <section class="block">
    <h1 v-if="account.name !== ''">
      Account: <span>{{ account.email }}</span>
    </h1>
    <h1 v-else>Account</h1>
    <Snackbar :option="snackbar"></Snackbar>
    <div class="form">
      <table>
        <tbody>
        <tr>
          <th>名</th>
          <td>
            <input type="text" class="field" v-model="account.first_name" />
          </td>
        </tr>
        <tr>
          <th>姓</th>
          <td>
            <input type="text" class="field" v-model="account.last_name" />
          </td>
        </tr>
        <tr>
          <th>メールアドレス</th>
          <td>
            <input type="text" class="field" v-model="account.email" />
          </td>
        </tr>
        <tr>
          <th>パスワード</th>
          <td>
            <input type="password" class="field" v-model="account.password" autocomplete="new-password" />
          </td>
        </tr>
        <tr v-if="loggedInUser.role === AccountRole.SUPER_ADMIN">
          <th>役割</th>
          <td>
            <select class="field" v-model="account.role">
              <option :value="AccountRole.ORGANIZATION_ADMIN">組織管理者</option>
              <option :value="AccountRole.USER">ユーザー</option>
            </select>
          </td>
        </tr>
        <tr v-if="!edit">
          <th>組織名</th>
          <td>
            <select class="field" v-model="account.organization">
              <option v-for="org in organization" :key="org.slug" :value="org.slug">{{ org.name }}</option>
            </select>
          </td>
        </tr>
        <tr v-if="edit">
          <th>グローバルロール</th>
          <td>
            {{ account.role_name }}
          </td>
        </tr>
        <tr v-if="edit && account.created">
          <th>作成日時</th>
          <td>{{ new Date(account.created).toLocaleString() }}</td>
        </tr>
        <tr v-if="edit && account.updated">
          <th>更新日時</th>
          <td>{{ new Date(account.updated).toLocaleString() }}</td>
        </tr>
        </tbody>
      </table>
      <nav>
        <button class="btn btn-secondary" v-if="!edit" @click="add">追加</button>
        <button class="btn btn-secondary" v-if="edit" @click="update">更新</button>
        <button class="btn btn-danger" v-if="edit" @click="remove">削除</button>
      </nav>
    </div>
  </section>
  <Dialog ref="remove" class="dialog">
    <h4 class="title">Confirm</h4>
    <div class="message">
      <p class="text">Do you really remove this account?</p>
    </div>
    <div class="action">
      <button class="btn btn-light" @click="removeCancel">Cancel</button>
      <button class="btn btn-danger" @click="removeConfirmed">削除</button>
    </div>
  </Dialog>
  <Dialog ref="error" class="dialog">
    <h4 class="title">Error</h4>
    <div class="message">
      <p class="text">{{ error.message }}</p>
    </div>
    <div class="action">
      <button class="btn btn-light" @click="errorClose">Close</button>
    </div>
  </Dialog>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import Dialog from '@/components/Dialog.vue';
import Snackbar from '@/components/Snackbar.vue';
import { Account, AccountData, AccountRole } from '@/lib/account';
import router from '@/router';
import { Organization } from '@/lib/organization';

export default defineComponent({
  name: 'AccountEditor',
  components: {
    Dialog,
    Snackbar,
  },
  props: {
    edit: Boolean,
    slug: String,
  },
  data() {
    return {
      loggedInUser: this.createDefaultAccount(),
      organization: [] as Organization[] | any,
      account: this.createDefaultAccount(),
      snackbar: {
        type: '',
        message: '',
      },
      error: {
        message: '',
        back: false,
      },
      AccountRole,
    };
  },
  async mounted() {
    try {
      if (this.slug) {
        this.account = await new Account(this.slug).get();
        if (!this.account) {
          this.error.message = 'このアカウントは見つかりませんでした';
          this.error.back = true;
          (this.$refs.error as InstanceType<typeof Dialog>)?.open();
        }
      }
      this.loggedInUser = await new Account().getByToken();
      this.organization = await new Organization().all();

      if (this.loggedInUser.role === AccountRole.ORGANIZATION_ADMIN) {
        this.account.organization = this.organization[0].slug || '';
      }

    } catch (e) {
      if (e instanceof Error) {
        this.error.message = e.message;
        this.error.back = true;
        (this.$refs.error as InstanceType<typeof Dialog>)?.open();
      }
      console.warn(e);
    }
  },
  methods: {
    createDefaultAccount() {
      return {
        slug: '',
        email: '',
        first_name: '',
        last_name: '',
        password: '',
        organization: '',
        role: AccountRole.USER,
        created: undefined,
        updated: undefined,
      };
    },
    getErrorMessage(): string {
      const { first_name, last_name, email, password } = this.account;
      if (!first_name?.trim()) return '名は空にできません！';
      if (!last_name?.trim()) return '姓は空にできません！';
      if (!email?.trim()) return 'メールアドレスは空にできません！';
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return '無効なメール形式です！';
      if (!this.edit && !password?.trim()) return 'パスワードは空にできません！';

      return '';
    },
    validate(): boolean {
      const errorMessage = this.getErrorMessage();
      if (!errorMessage) return true;
      this.snackbar.type = 'warning';
      this.snackbar.message = errorMessage;

      return false;
    },
    async add() {
      if (!this.validate()) {
        return;
      }
      try {
        const ac = new Account();
        const slug = await ac.add(this.account as AccountData);
        if (slug) {
          console.info('Account added!');
          await router.replace(`/accounts/${slug}`);
        } else {
          this.snackbar.type = 'warning';
          this.snackbar.message = 'アカウントの作成に失敗しました！';
        }
      } catch (e) {
        if (e instanceof Error) {
          this.error.message = `アカウントの作成に失敗しました！ ${e.message}`;
          (this.$refs.error as InstanceType<typeof Dialog>)?.open();
        }
        console.warn(e);
      }
    },
    async update() {
      if (!this.validate()) {
        return;
      }
      try {
        const ac = new Account(this.slug);
        const slug = await ac.update(this.account as AccountData);
        if (slug) {
          this.snackbar.type = 'success';
          this.snackbar.message = 'アカウントが更新されました！';
        } else {
          this.snackbar.type = 'warning';
          this.snackbar.message = 'このアカウントの更新に失敗しました！';
        }
      } catch (e) {
        if (e instanceof Error) {
          this.error.message = `Fail to update account! ${e.message}`;
          (this.$refs.error as InstanceType<typeof Dialog>)?.open();
        }
        console.warn(e);
      }
    },
    remove() {
      (this.$refs.remove as InstanceType<typeof Dialog>)?.open();
    },
    async removeConfirmed() {
      (this.$refs.remove as InstanceType<typeof Dialog>)?.close();
      try {
        const org = new Account(this.slug);
        if (await org.delete()) {
          console.info('Account deleted!');
          router.go(-1);
        } else {
          this.snackbar.type = 'warning';
          this.snackbar.message = 'このアカウントの削除に失敗しました！';
        }
      } catch (e) {
        if (e instanceof Error) {
          this.error.message = e.message;
          (this.$refs.error as InstanceType<typeof Dialog>)?.open();
        }
        console.warn(e);
      }
    },
    removeCancel() {
      (this.$refs.remove as InstanceType<typeof Dialog>)?.close();
    },
    errorClose() {
      (this.$refs.error as InstanceType<typeof Dialog>)?.close();
      this.error.back && this.$router.back();
    },
  },
});
</script>

<style lang="scss" scoped>
@use '@/assets/const.scss' as *;
@use '@/assets/dialog.scss' as *;

.block {
  .form {
    margin: 0 10px;

    @media (min-width: $tablet) {
      margin: 0 20px;
    }

    @media (min-width: $desktop) {
      margin: 0 40px;
    }

    table {
      width: 100%;
      max-width: 640px;
    }

    th {
      padding: 5px;
      font-weight: normal;
    }

    td {
      padding: 5px;
    }

    .field {
      width: 100%;
    }

    nav {
      display: flex;
      margin-top: 20px;

      button {
        margin-right: 20px;

        &:last-child:not(:first-child) {
          margin-left: auto;
          margin-right: 0;
        }
      }
    }
  }
}

.dialog {
  @include dialog();
}
</style>